import { useEffect, useState } from "react";
import { MiniPhoneMockup } from "./MiniPhoneMockup";

interface MobileWaitlistCardProps {
	email: string;
	agentEmail?: string;
	onJoinWaitlist: () => void;
	onDismiss: (reason: "close" | "maybe_later") => void;
	onClose: () => void;
}

export function MobileWaitlistCard({
	email,
	agentEmail,
	onJoinWaitlist,
	onDismiss,
	onClose,
}: MobileWaitlistCardProps) {
	const [joined, setJoined] = useState(false);

	const handleJoin = () => {
		setJoined(true);
		onJoinWaitlist();
	};

	// Auto-dismiss 3s after joining the waitlist
	useEffect(() => {
		if (!joined) return;
		const timer = setTimeout(() => {
			onClose();
		}, 5000);
		return () => clearTimeout(timer);
	}, [joined, onClose]);

	const handleClose = () => {
		if (joined) {
			onClose();
		} else {
			onDismiss("close");
		}
	};

	return (
		<div className="fixed bottom-5 right-5 z-50 w-[375px] overflow-hidden rounded-2xl bg-white shadow-2xl">
			{/*
			 * Header area — the blue gradient only fills the top ~50%, then fades
			 * to white so the phone appears to float over the transition.
			 */}
			<div className="relative h-[220px] overflow-hidden">
				{/* Blue gradient — light blue at top, deep blue at bottom */}
				<div
					className="absolute inset-0"
					style={{
						background:
							"linear-gradient(to bottom, #5E9BF6 0%, #4A8AF3 35%, #3575EE 65%, #2563EB 100%)",
					}}
				/>

				{/* Close button */}
				<button
					type="button"
					onClick={handleClose}
					className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/30"
					aria-label="Close"
				>
					<svg
						className="h-3.5 w-3.5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth={2.5}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				</button>

				{/* NEW badge */}
				<div className="absolute left-3 top-3 z-10 flex items-center gap-2">
					<span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-white backdrop-blur-sm">
						NEW
					</span>
				</div>

				{/* Phone mockup — zoomed in to fill the blue header area */}
				<div className="absolute left-1/2 top-4 z-[1] -translate-x-1/2">
					<div className="scale-[1.05] origin-top">
						<MiniPhoneMockup />
					</div>
				</div>
			</div>

			{/* Card body */}
			<div className="min-h-[227px] px-5 pb-5 pt-4">
				{joined ? (
					/* Success state */
					<div className="text-center">
						<div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
							<svg
								className="h-6 w-6 text-green-600"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								strokeWidth={2.5}
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M5 13l4 4L19 7"
								/>
							</svg>
						</div>
						<h3 className="mb-1 text-lg font-bold text-gray-900">
							You're on the list
						</h3>
						<p className="mb-3 text-sm text-gray-500">
							We'll email you the moment Amber Flex mobile goes live.
						</p>
						<p className="text-sm text-gray-500">
							Notifying{" "}
							<span className="font-medium text-green-600">
								{agentEmail && agentEmail !== "--" ? agentEmail : email}
							</span>
						</p>
					</div>
				) : (
					/* Default state */
					<>
						<h3 className="mb-1.5 text-lg font-bold text-gray-900">
							Amber Flex is coming to mobile
						</h3>
						<p className="mb-5 text-sm leading-relaxed text-gray-500">
							Track leads, bookings and analytics on the go. Be first in line
							when the app launches.
						</p>
						<button
							type="button"
							onClick={handleJoin}
							className="mb-3 w-full rounded-xl bg-[#2563EB] py-3.5 text-left pl-5 text-sm font-semibold text-white transition-colors hover:bg-[#1D4FD7]"
						>
							Join the waitlist
						</button>
						<button
							type="button"
							onClick={() => onDismiss("maybe_later")}
							className="w-full py-1.5 text-sm text-gray-400 transition-colors hover:text-gray-600"
						>
							Maybe later
						</button>
					</>
				)}
			</div>
		</div>
	);
}
